import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  MessageSquare, Plus, Search, Users, Send, ArrowLeft, Circle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

const EmployeeChatPage = () => {
  const { t } = useTranslation();
  const [currentUser, setCurrentUser] = useState(null);
  const [chatRooms, setChatRooms] = useState([]);
  const [colleagues, setColleagues] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('movemate_user') || '{}');
    setCurrentUser(user);
    fetchChatRooms();
  }, []);

  useEffect(() => {
    if (selectedRoom) {
      fetchMessages(selectedRoom.id);
      markAsRead(selectedRoom.id);
    }
  }, [selectedRoom]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchChatRooms = async () => {
    try {
      const response = await api.get('/employee-chat/rooms');
      setChatRooms(response.data.rooms || []);
    } catch (error) {
      console.error('Failed to fetch chat rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchColleagues = async () => {
    try {
      const response = await api.get('/employee-chat/colleagues');
      setColleagues(response.data.colleagues || []);
    } catch (error) {
      console.error('Failed to fetch colleagues:', error);
    }
  };

  const fetchMessages = async (roomId) => {
    try {
      const response = await api.get(`/employee-chat/rooms/${roomId}/messages`);
      setMessages(response.data.messages || []);
    } catch (error) {
      toast.error(t('errors.failed_to_load'));
    }
  };

  const markAsRead = async (roomId) => {
    try {
      await api.post(`/employee-chat/rooms/${roomId}/read`);
      // Update local unread count
      setChatRooms(prev => prev.map(room => 
        room.id === roomId ? { ...room, my_unread: 0 } : room
      ));
    } catch (error) {
      console.error('Failed to mark as read');
    }
  };

  const handleStartNewChat = async (colleagueId) => {
    try {
      const response = await api.post('/employee-chat/rooms', {
        participant_id: colleagueId,
        room_type: 'direct'
      });
      
      const room = response.data.room;
      if (response.data.existing) {
        toast.info('Opening existing conversation');
      } else {
        toast.success(t('success_messages.created'));
        setChatRooms(prev => [room, ...prev]);
      }
      
      setSelectedRoom(room);
      setShowNewChat(false);
    } catch (error) {
      toast.error(t('errors.something_went_wrong'));
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedRoom) return;

    setSending(true);
    try {
      const response = await api.post(`/employee-chat/rooms/${selectedRoom.id}/messages`, {
        content: newMessage,
        message_type: 'text'
      });
      
      setMessages(prev => [...prev, { ...response.data, is_mine: true }]);
      setNewMessage('');
      
      // Update room's last message
      setChatRooms(prev => prev.map(room => 
        room.id === selectedRoom.id 
          ? { ...room, last_message: newMessage, last_message_at: new Date().toISOString() }
          : room
      ));
    } catch (error) {
      toast.error(t('errors.something_went_wrong'));
    } finally {
      setSending(false);
    }
  };

  const handleOpenNewChat = () => {
    fetchColleagues();
    setShowNewChat(true);
  };

  const filteredColleagues = colleagues.filter(c => {
    const fullName = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
    return fullName.includes(searchQuery.toLowerCase());
  });

  const getRoleBadge = (role) => {
    const roleColors = {
      'company_admin': 'bg-purple-100 text-purple-800',
      'manager': 'bg-blue-100 text-blue-800',
      'driver_crew': 'bg-green-100 text-green-800',
      'porter': 'bg-orange-100 text-orange-800',
      'sales_rep': 'bg-cyan-100 text-cyan-800'
    };
    return roleColors[role] || 'bg-gray-100 text-gray-800';
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return formatTime(dateString);
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="h-[calc(100vh-180px)]" data-testid="employee-chat-page">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-2xl font-bold">{t('nav.messages')}</h1>
          <p className="text-gray-600">Chat with your team members</p>
        </div>
        <Button onClick={handleOpenNewChat} data-testid="new-employee-chat-btn">
          <Plus size={16} className="mr-2" />
          {t('chat.new_conversation')}
        </Button>
      </div>

      <div className="flex gap-4 h-full">
        {/* Chat List */}
        <Card className="w-80 flex flex-col">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users size={18} />
              Team Chats
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0">
            {loading ? (
              <div className="p-4 text-center text-gray-500">{t('common.loading')}</div>
            ) : chatRooms.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <MessageSquare size={48} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">{t('chat.no_messages')}</p>
                <Button 
                  variant="link" 
                  className="mt-2"
                  onClick={handleOpenNewChat}
                >
                  {t('chat.start_conversation')}
                </Button>
              </div>
            ) : (
              <ScrollArea className="h-[calc(100vh-320px)]">
                <div className="divide-y">
                  {chatRooms.map((room) => (
                    <div
                      key={room.id}
                      onClick={() => setSelectedRoom(room)}
                      className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedRoom?.id === room.id ? 'bg-primary/10 border-l-4 border-primary' : ''
                      }`}
                      data-testid={`chat-room-${room.id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">
                            {room.other_participant_name || room.name || 'Team Chat'}
                          </p>
                          {room.last_message && (
                            <p className="text-sm text-gray-500 truncate">
                              {room.last_message}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1 ml-2">
                          <span className="text-xs text-gray-400">
                            {formatDate(room.last_message_at)}
                          </span>
                          {room.my_unread > 0 && (
                            <Badge className="bg-primary text-white text-xs px-1.5 py-0">
                              {room.my_unread}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* Chat Window */}
        <Card className="flex-1 flex flex-col">
          {selectedRoom ? (
            <>
              <CardHeader className="border-b pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="md:hidden"
                      onClick={() => setSelectedRoom(null)}
                    >
                      <ArrowLeft size={18} />
                    </Button>
                    <div>
                      <CardTitle className="text-lg">
                        {selectedRoom.other_participant_name || selectedRoom.name || 'Team Chat'}
                      </CardTitle>
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="flex-1 overflow-hidden p-0">
                <ScrollArea className="h-[calc(100vh-400px)] p-4">
                  <div className="space-y-4">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.is_mine ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg px-4 py-2 ${
                            msg.is_mine
                              ? 'bg-primary text-white'
                              : 'bg-gray-100 text-gray-900'
                          }`}
                        >
                          {!msg.is_mine && (
                            <p className="text-xs font-semibold mb-1 opacity-70">
                              {msg.sender_name}
                            </p>
                          )}
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          <p className={`text-xs mt-1 ${msg.is_mine ? 'text-white/70' : 'text-gray-400'}`}>
                            {formatTime(msg.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
              </CardContent>

              <div className="p-4 border-t">
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={t('chat.type_message')}
                    className="flex-1"
                    data-testid="message-input"
                  />
                  <Button type="submit" disabled={sending || !newMessage.trim()} data-testid="send-message-btn">
                    <Send size={18} />
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <MessageSquare size={64} className="mx-auto mb-4 opacity-20" />
                <p className="text-lg font-medium">Select a conversation</p>
                <p className="text-sm">Choose a chat from the list or start a new one</p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* New Chat Dialog */}
      <Dialog open={showNewChat} onOpenChange={setShowNewChat}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="text-primary" size={20} />
              {t('chat.new_conversation')}
            </DialogTitle>
            <DialogDescription>
              Select a team member to start chatting
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <Input
                placeholder={t('common.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <div className="max-h-64 overflow-y-auto border rounded-lg">
              {filteredColleagues.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <Users size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No team members found</p>
                </div>
              ) : (
                <div className="divide-y">
                  {filteredColleagues.map((colleague) => (
                    <div
                      key={colleague.id}
                      onClick={() => handleStartNewChat(colleague.id)}
                      className="p-3 cursor-pointer hover:bg-gray-50 flex items-center justify-between"
                      data-testid={`colleague-${colleague.id}`}
                    >
                      <div>
                        <p className="font-medium">
                          {colleague.first_name} {colleague.last_name}
                        </p>
                        <Badge className={`text-xs ${getRoleBadge(colleague.role)}`}>
                          {t(`staff.roles.${colleague.role.replace('_crew', '')}`) || colleague.role}
                        </Badge>
                      </div>
                      {colleague.is_clocked_in && (
                        <Circle size={10} className="fill-green-500 text-green-500" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewChat(false)}>
              {t('common.cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeChatPage;
